import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import { printReceipt } from "../utils/receiptPrinter";
import axiosClient from "../api/axiosClient";

export type PaymentId = "cash" | "card" | "gift-card" | "card-on-file" | "manual-entry";
export type ScreenId =
  | "payment_method"
  | "cash"
  | "card"
  | "gift-card"
  | "card-on-file"
  | "manual-entry"
  | "printing"
  | "done";

const paymentMethodToApi: Record<PaymentId, string> = {
  cash: "cash",
  card: "card",
  "gift-card": "gift_card",
  "card-on-file": "card_on_file",
  "manual-entry": "manual_entry",
};

type PosFlowState = {
  screen: ScreenId;
  amountDue: number; // pre-tax subtotal
  taxRate: number; // decimal, e.g. 0.08 for 8%
  selectedPayment: PaymentId | null;

  // Threaded through from AdminSalesPage.handleCheckout so the checkout call and
  // the receipt have real booking/client/service data instead of just an amount.
  bookingId: string | null;
  clientId: string | null;
  clientName: string | null;
  service: string | null;
  staffId: string | null;

  cashReceived: number; // cash entered
  changeDue: number; // computed

  lastReceiptId: string | null;
  lastTransactionId: string | null;
  error: string | null;
};

const initialState: PosFlowState = {
  screen: "payment_method",
  amountDue: 0.0,
  taxRate: 0,
  selectedPayment: "cash",

  bookingId: null,
  clientId: null,
  clientName: null,
  service: null,
  staffId: null,

  cashReceived: 0,
  changeDue: 0,

  lastReceiptId: null,
  lastTransactionId: null,
  error: null,
};

function authHeader(): Record<string, string> {
  const token = window.localStorage.getItem("accessToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function computeTotal(amountDue: number, taxRate: number): number {
  return Number((amountDue * (1 + taxRate)).toFixed(2));
}

export const fetchTaxRate = createAsyncThunk<number, void>("posFlow/fetchTaxRate", async () => {
  const res = await axiosClient.get("/business", { headers: authHeader() });
  return (res.data?.tax_rate as number) ?? 0;
});

type CheckoutExtra = {
  tenderedAmount?: number;
  changeDue?: number;
  giftCardCode?: string;
  cardOnFileId?: string;
};

async function submitCheckout(
  state: RootState["pos"],
  paymentMethod: PaymentId,
  extra: CheckoutExtra
): Promise<{ receiptId: string; transactionId: string }> {
  if (!state.bookingId) {
    throw new Error("No booking selected for checkout.");
  }

  const total = computeTotal(state.amountDue, state.taxRate);

  const res = await axiosClient.post(
    `/bookings/${state.bookingId}/checkout`,
    {
      payment_method: paymentMethodToApi[paymentMethod],
      amount: total,
      tendered_amount: extra.tenderedAmount,
      change_due: extra.changeDue,
      client_id: state.clientId,
      staff_id: state.staffId,
      gift_card_code: extra.giftCardCode,
      card_on_file_id: extra.cardOnFileId,
    },
    { headers: authHeader() }
  );

  const transactionId = res.data.transaction_id as string;
  const tax = Number((total - state.amountDue).toFixed(2));

  await printReceipt(
    {
      receiptId: transactionId,
      amountDue: total,
      cashReceived: extra.tenderedAmount ?? total,
      changeDue: extra.changeDue ?? 0,
      paidAtIso: new Date().toISOString(),
      lineItems: state.service ? [{ name: state.service, price: state.amountDue }] : undefined,
      footerLines: state.taxRate > 0 ? [`Tax (${(state.taxRate * 100).toFixed(1)}%): $${tax.toFixed(2)}`] : undefined,
    },
    { fallbackToBrowserPrint: true }
  );

  return { receiptId: transactionId, transactionId };
}

export const completeCashAndPrintReceipt = createAsyncThunk<
  { receiptId: string; transactionId: string },
  void,
  { state: RootState }
>("posFlow/completeCashAndPrintReceipt", async (_, thunkApi) => {
  const state = thunkApi.getState().pos;
  if (state.cashReceived < computeTotal(state.amountDue, state.taxRate)) {
    throw new Error("Insufficient cash received.");
  }
  return submitCheckout(state, "cash", {
    tenderedAmount: state.cashReceived,
    changeDue: state.changeDue,
  });
});

export const completeTerminalCardSale = createAsyncThunk<
  { receiptId: string; transactionId: string },
  void,
  { state: RootState }
>("posFlow/completeTerminalCardSale", async (_, thunkApi) => {
  const state = thunkApi.getState().pos;
  return submitCheckout(state, "card", {});
});

export const completeGiftCardSale = createAsyncThunk<
  { receiptId: string; transactionId: string },
  { code: string },
  { state: RootState }
>("posFlow/completeGiftCardSale", async ({ code }, thunkApi) => {
  const state = thunkApi.getState().pos;
  return submitCheckout(state, "gift-card", { giftCardCode: code });
});

export const completeCardOnFileSale = createAsyncThunk<
  { receiptId: string; transactionId: string },
  { cardOnFileId: string },
  { state: RootState }
>("posFlow/completeCardOnFileSale", async ({ cardOnFileId }, thunkApi) => {
  const state = thunkApi.getState().pos;
  return submitCheckout(state, "card-on-file", { cardOnFileId });
});

export const completeManualEntrySale = createAsyncThunk<
  { receiptId: string; transactionId: string },
  void,
  { state: RootState }
>("posFlow/completeManualEntrySale", async (_, thunkApi) => {
  const state = thunkApi.getState().pos;
  return submitCheckout(state, "manual-entry", {});
});

const posFlowSlice = createSlice({
  name: "posFlow",
  initialState,
  reducers: {
    setAmountDue(state, action: PayloadAction<number>) {
      state.amountDue = action.payload;
    },
    setSelectedPayment(state, action: PayloadAction<PaymentId | null>) {
      state.selectedPayment = action.payload;
    },
    goToScreen(state, action: PayloadAction<ScreenId>) {
      state.screen = action.payload;
      state.error = null;
    },
    setCashReceived(state, action: PayloadAction<number>) {
      const received = action.payload;
      state.cashReceived = received;

      const diff = received - computeTotal(state.amountDue, state.taxRate);
      state.changeDue = diff > 0 ? Number(diff.toFixed(2)) : 0;
    },
    setCheckoutContext(
      state,
      action: PayloadAction<{
        bookingId: string;
        clientId: string | null;
        clientName: string;
        service: string;
        staffId: string;
        amountDue: number;
      }>
    ) {
      state.bookingId = action.payload.bookingId;
      state.clientId = action.payload.clientId;
      state.clientName = action.payload.clientName;
      state.service = action.payload.service;
      state.staffId = action.payload.staffId;
      state.amountDue = action.payload.amountDue;
    },
    resetFlow(state) {
      state.screen = "payment_method";
      state.selectedPayment = "cash";
      state.bookingId = null;
      state.clientId = null;
      state.clientName = null;
      state.service = null;
      state.staffId = null;
      state.cashReceived = 0;
      state.changeDue = 0;
      state.lastReceiptId = null;
      state.lastTransactionId = null;
      state.error = null;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    const originatingScreen: Record<string, ScreenId> = {
      "posFlow/completeCashAndPrintReceipt": "cash",
      "posFlow/completeTerminalCardSale": "card",
      "posFlow/completeGiftCardSale": "gift-card",
      "posFlow/completeCardOnFileSale": "card-on-file",
      "posFlow/completeManualEntrySale": "manual-entry",
    };

    [
      completeCashAndPrintReceipt,
      completeTerminalCardSale,
      completeGiftCardSale,
      completeCardOnFileSale,
      completeManualEntrySale,
    ].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.screen = "printing";
          state.error = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.lastReceiptId = action.payload.receiptId;
          state.lastTransactionId = action.payload.transactionId;
          state.screen = "done";
        })
        .addCase(thunk.rejected, (state, action) => {
          state.screen = originatingScreen[thunk.typePrefix] ?? "payment_method";
          state.error = action.error.message ?? "Payment failed.";
        });
    });

    builder.addCase(fetchTaxRate.fulfilled, (state, action) => {
      state.taxRate = action.payload;
    });
  },
});

export const {
  setAmountDue,
  setSelectedPayment,
  goToScreen,
  setCashReceived,
  setCheckoutContext,
  resetFlow,
  setError,
} = posFlowSlice.actions;

export default posFlowSlice.reducer;

export const selectPosFlow = (s: RootState) => s.pos;

export const selectCheckoutTotals = (s: RootState) => {
  const { amountDue, taxRate } = s.pos;
  const total = computeTotal(amountDue, taxRate);
  return {
    subtotal: amountDue,
    taxRate,
    tax: Number((total - amountDue).toFixed(2)),
    total,
  };
};
